import {
  landFilterDefaults,
  landFilterFields,
  ROUTE_DOCS_LANDS,
  ROUTE_DOCS_MONITORING,
  ROUTE_DOCS_PROJECTS,
} from '@/common/constants';
import { IRegion } from '@/common/models/IStore';
import useLandStore from '@/features/lands/store/useLandStore';
import { useMonitoringStore } from '@/features/monitoring/store/useMonitoringStore';
import useProjectStore from '@/features/projects/store/useProjectStore';
import useStore from '@/lib/store';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';

export default function useAgrotechFilter({ page }: { page: string }) {
  const { status, district_id, region_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchLands } = useLandStore();
  const { fetchProjectList } = useProjectStore();
  const { fetchMonitoringFarmers } = useMonitoringStore();
  const defaultValues = useMemo(() => {
    const values: Record<string, any> = { ...landFilterDefaults };

    landFilterFields.forEach((field) => {
      const param = searchParams.get(field);
      if (param !== null) {
        values[field] = param;
      }
    });

    return values;
  }, [searchParams]);

  const form = useForm<any>({
    //  resolver: zodResolver(filterSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const { control, watch, handleSubmit, reset, setValue } = form;
  const regionId = watch('region_id');
  const { user, region, district, fetchDistrict, fetchRegion, plants } =
    useStore();
  useEffect(() => {
    fetchRegion();
  }, []);

  useEffect(() => {
    if (regionId) {
      fetchDistrict({
        region_id: String(
          region?.find((item) => item.code === Number(regionId))?.id,
        ),
      });
      setValue('district_id', '');
    }
  }, [regionId, setValue]);
  useEffect(() => {
    if (region_id) {
      fetchDistrict({ region_id });
    }
  }, [region_id]);

  const region_soato = useMemo(() => {
    return region?.find((item) => item.id === region_id)?.code;
  }, [region_id, region]);

  const district_soato = useMemo(() => {
    return district?.find((item) => item.id === district_id)?.code;
  }, [district_id, district]);

  const sizeParam = Number(searchParams.get('size')) || 20;
  const pageParam = Number(searchParams.get('page')) || 1;
  const regionIdParam = searchParams.get('region_id');
  const districtIdParam = searchParams.get('district_id');
  const farmerParam = searchParams.get('farmer');
  const innParam = Number(searchParams.get('inn')) || undefined;
  const contourParam = searchParams.get('contour_number');
  const cadastreParam = searchParams.get('cadastre');
  const regNumberParam = searchParams.get('reg_number');
  const areaFromParam = searchParams.get('area_from');
  const areaToParam = searchParams.get('area_to');
  const cropIdParam = searchParams.get('crop_id');
  const plant_id =
    page === ROUTE_DOCS_LANDS ? undefined : searchParams.get('plant_id') || '1';
  const year = searchParams.get('year') || '2026';

  const query = useMemo(() => {
    return {
      size: sizeParam,
      page: pageParam,
      region_soato:
        page === ROUTE_DOCS_MONITORING
          ? region_soato
          : regionIdParam || undefined,
      district_soato:
        page === ROUTE_DOCS_MONITORING
          ? district_soato
          : districtIdParam || undefined,
      farmer: farmerParam || undefined,
      inn: innParam,
      contour_number: contourParam || undefined,
      cadastre: cadastreParam || undefined,
      reg_number: regNumberParam || undefined,
      area_from: areaFromParam || undefined,
      area_to: areaToParam || undefined,
      crop_id: cropIdParam || undefined,
      plant_id: plant_id || undefined,
      year,
    };
  }, [
    page,
    region_soato,
    district_soato,
    sizeParam,
    pageParam,
    regionIdParam,
    districtIdParam,
    farmerParam,
    innParam,
    contourParam,
    cadastreParam,
    regNumberParam,
    areaFromParam,
    areaToParam,
    cropIdParam,
    plant_id,
    year,
  ]);

  useEffect(() => {
    if (page === ROUTE_DOCS_LANDS) {
      fetchLands({ status, query });
    } else if (page === ROUTE_DOCS_PROJECTS) {
      fetchProjectList({ query });
    } else {
      fetchMonitoringFarmers({ query });
    }
  }, [status, query, page]);

  const regionList = useMemo(() => region?.filter((item) => item.id), [region]);
  const districtList = useMemo(
    () => district?.filter((item) => item.id),
    [district],
  );

  const plantsList = useMemo(
    () =>
      plants?.map((item) => ({
        value: String(item.id),
        label: item.name,
      })),
    [plants],
  );

  const selectFormat = useCallback(
    (item: IRegion) => ({
      value: String(item.code),
      label: item.name_uz,
    }),
    [],
  );

  const removeEmptyValues = (obj: Record<string, any>): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
  };

  const clearFilter = () => {
    reset(landFilterDefaults);
    setSearchParams((prev) => {
      landFilterFields.forEach((field) => prev.delete(field));
      return prev;
    });
  };

  const onSubmit = (values: any) => {
    const params = removeEmptyValues(values);
    setSearchParams((prev) => {
      landFilterFields.forEach((field) => {
        if (params[field]) prev.set(field, params[field]);
        else prev.delete(field);
      });
      return prev;
    });
  };

  return {
    form,
    control,
    user,
    regionList,
    districtList,
    selectFormat,
    clearFilter,
    plantsList,
    handleForm: handleSubmit(onSubmit),
  };
}
